import { useEffect, useState } from "react";
import { MapPin, Briefcase, Loader2, UserPlus, Check, Clock, UserCheck, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Members() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [statusMap, setStatusMap] = useState({}); // user_id -> 'none'|'pending_outgoing'|'pending_incoming'|'friends'
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const loadStatuses = async (list) => {
    const targets = list.filter((u) => u.id !== me?.id);
    const results = await Promise.all(
      targets.map(async (u) => {
        try {
          const { data } = await api.get(`/friends/status/${u.id}`);
          return [u.id, data];
        } catch {
          return [u.id, { status: "none" }];
        }
      })
    );
    const map = {};
    for (const [id, val] of results) map[id] = val;
    setStatusMap(map);
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/users");
        setUsers(data);
        loadStatuses(data);
      } catch (err) {
        console.error("Failed to load members", err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendRequest = async (toId) => {
    try {
      await api.post("/friends/request", { to_user_id: toId });
      toast.success("Freundschaftsanfrage gesendet ✉️");
      setStatusMap({ ...statusMap, [toId]: { status: "pending_outgoing" } });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler");
    }
  };

  const filtered = users.filter(
    (u) =>
      u.name.toLowerCase().includes(query.toLowerCase()) ||
      (u.location || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div data-testid="members-page" className="space-y-4 sm:space-y-6">
      <div>
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#4DBFFF] mb-1">Crew</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-black text-slate-900 tracking-tighter">
          Mitglieder 👥
        </h1>
        <p className="text-xs sm:text-sm text-slate-600 mt-1">
          Alle Fahrzeugüberführer in der TSU-PARTNERS Community.
        </p>
      </div>

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Suche nach Name oder Standort…"
        data-testid="members-search-input"
        className="w-full bg-[#FFFFFF] border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-600 focus:outline-none focus:border-[#1D5BFF] focus:ring-2 focus:ring-[#1D5BFF]/30"
      />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lade Mitglieder…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((u) => {
            const st = statusMap[u.id]?.status || "none";
            const isMe = me?.id === u.id;
            return (
              <div
                key={u.id}
                data-testid={`member-card-${u.id}`}
                className="bg-[#FFFFFF] border border-slate-200 rounded-2xl p-5 hover:border-[#1D5BFF]/50 hover:shadow-[0_8px_40px_rgba(0,102,255,0.15)] transition-all"
              >
                <div className="flex items-center gap-4">
                  <img
                    src={fileUrl(u.avatar_url)}
                    alt={u.name}
                    className="w-14 h-14 rounded-full object-cover border border-slate-200"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-slate-900 truncate flex items-center gap-1.5 flex-wrap">
                      {u.name}
                      {u.is_support && (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold">
                          TSU Support
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">
                      {u.role === "admin" ? "Administrator" : u.role === "support" ? "Support-Team" : "Fahrer"}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center flex-wrap gap-3 text-xs text-slate-600">
                  {u.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-[#4DBFFF]" /> {u.location}
                    </span>
                  )}
                  {u.experience_years != null && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="w-3.5 h-3.5 text-[#4DBFFF]" /> {u.experience_years} J.
                    </span>
                  )}
                </div>
                {u.bio && <p className="text-sm text-slate-700 mt-3 line-clamp-3">{u.bio}</p>}

                {!isMe && (
                  <div className="mt-4">
                    {st === "friends" ? (
                      <button
                        onClick={() => navigate(`/nachrichten/${u.id}`)}
                        data-testid={`message-${u.id}`}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#1D5BFF] text-white text-sm font-semibold py-2 rounded-full hover:bg-[#1546C9]"
                      >
                        <MessageCircle className="w-4 h-4" /> Nachricht senden
                      </button>
                    ) : st === "pending_outgoing" ? (
                      <button
                        disabled
                        data-testid={`pending-out-${u.id}`}
                        className="w-full inline-flex items-center justify-center gap-2 bg-slate-100/70 text-slate-600 text-sm font-semibold py-2 rounded-full cursor-not-allowed"
                      >
                        <Clock className="w-4 h-4" /> Anfrage gesendet
                      </button>
                    ) : st === "pending_incoming" ? (
                      <button
                        onClick={() => navigate("/freunde")}
                        data-testid={`pending-in-${u.id}`}
                        className="w-full inline-flex items-center justify-center gap-2 bg-amber-500/15 border border-amber-500/30 text-amber-300 text-sm font-semibold py-2 rounded-full hover:bg-amber-500/25"
                      >
                        <UserCheck className="w-4 h-4" /> Anfrage beantworten
                      </button>
                    ) : (
                      <button
                        onClick={() => sendRequest(u.id)}
                        data-testid={`send-request-${u.id}`}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#1D5BFF]/10 hover:bg-[#1D5BFF] hover:text-white text-[#4DBFFF] text-sm font-semibold py-2 rounded-full transition-all border border-[#1D5BFF]/30"
                      >
                        <UserPlus className="w-4 h-4" /> Freundschaftsanfrage
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-slate-500 py-12">
              Keine Mitglieder gefunden.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
